import {useQuery, UseQueryResult} from '@tanstack/react-query';
import {useMemo} from 'react';

import {ReviewsStateType} from '../../page/main/reviews/reviews-state/reviews-state-type';
import {useReviewsStatus, useReviewStatus} from '../../page/main/reviews/reviews-state/reviews-status-hook';
import {useUser} from '../../provider/user/user-hook';
import {deserializeV2, postAndDeserialize} from '../../util/api-adapter';
import {useCursorPagination} from '../../util/cursor-pagination/cursor-pagination-hook';
import {CursorPaginationDataType, CursorPaginationType} from '../../util/cursor-pagination/cursor-pagination-type';
import {createRaceFetchX, FetchMethodEnum, fetchX} from '../../util/fetch';
import {objectToUrlParameters} from '../../util/url';
import {getCsrfHeaders, mainApiHeaders, rootApiUrl} from '../api/api-const';

import {reviewsStateToUrlObject} from './reviews-helper';
import {
    BulkReviewNotesRequestType,
    bulkReviewNotesResultSchema,
    BulkReviewNotesResultType,
    BulkReviewTagsRequestType,
    bulkReviewTagsResultSchema,
    BulkReviewTagsResultType,
    fetchReviewsResultSchema,
    FetchReviewsResultType,
    reviewDataSchema,
    ReviewDataType,
    ReviewSentimentType,
    ReviewsReplyTagsType,
    ReviewTagsPutType,
} from './reviews-type';

const reviewsRaceFetchX = createRaceFetchX();

function getReviewsUrl(reviewOption: ReviewsStateType, paginationData: CursorPaginationDataType) {
    const {pageSize, ...restPaginationData} = paginationData;

    const urlParameters = objectToUrlParameters({
        ...(!reviewOption.company_ids ? {filter_key: reviewOption.filterId} : null),
        ...reviewsStateToUrlObject(reviewOption),
        per_page: pageSize,
        ...restPaginationData,
    });

    return `${rootApiUrl}/v4/reviews/?${urlParameters}`;
}

async function fetchReviews(
    reviewOption: ReviewsStateType,
    paginationData: CursorPaginationDataType
): Promise<FetchReviewsResultType> {
    const url = getReviewsUrl(reviewOption, paginationData);

    const response = await reviewsRaceFetchX(url);

    return deserializeV2<FetchReviewsResultType>(url, fetchReviewsResultSchema, response);
}

export function useReviews(
    reviewsState: ReviewsStateType
): UseQueryResult<FetchReviewsResultType> & CursorPaginationType & {queryKey: string; reviewIds: Array<number>} {
    const paginationDependencies = useMemo(() => {
        return {mainFilterKey: reviewsState.filterId, reviewsState};
    }, [reviewsState]);

    const cursorPagination = useCursorPagination({
        dependencies: paginationDependencies,
    });
    const {pageSize, cursor, onDataLoaded} = cursorPagination;
    const {user} = useUser();
    const queryKey = `${getReviewsUrl(reviewsState, {cursor, pageSize})}${user?.profile?.isShowAutotags}`;
    const query = useQuery([queryKey], () => fetchReviews(reviewsState, {cursor, pageSize}), {
        onSuccess: (data) => onDataLoaded(data),
        refetchOnMount: false,
        keepPreviousData: true,
        refetchOnWindowFocus: false,
    });

    const reviewsIds = query.data?.results.map((review) => review.id) || [];

    useReviewsStatus(reviewsIds, getReviewsUrl(reviewsState, {cursor, pageSize}));

    return {
        ...query,
        pagination: cursorPagination,
        queryKey,
        reviewIds: reviewsIds,
    };
}

function getReviewItemUrl(id: number) {
    return `${rootApiUrl}/v4/reviews/${id}/`;
}

export async function fetchReviewItem(id: number): Promise<ReviewDataType> {
    const url = getReviewItemUrl(id);
    const response = await fetchX<ReviewDataType>(url);

    return deserializeV2<ReviewDataType>(url, reviewDataSchema, response);
}

export function useReviewItem(id: number): UseQueryResult<ReviewDataType> & {queryKey: string} {
    const query = useQuery([getReviewItemUrl(id)], () => fetchReviewItem(id), {
        refetchOnWindowFocus: false,
    });

    useReviewStatus(getReviewItemUrl(id), query.data?.id);

    return {...query, queryKey: getReviewItemUrl(id)};
}

export function fetchReviewsReplyTagsSend(reviewId: number, data: ReviewsReplyTagsType): Promise<ReviewTagsPutType> {
    return fetchX<ReviewTagsPutType>(`${rootApiUrl}/v4/reviews/${reviewId}/tags/`, {
        method: FetchMethodEnum.put,
        headers: {...mainApiHeaders, ...getCsrfHeaders()},
        body: JSON.stringify(data),
    });
}

export function postBulkReviewTags(payload: BulkReviewTagsRequestType): Promise<BulkReviewTagsResultType> {
    return postAndDeserialize<BulkReviewTagsResultType>('/v4/reviews/tags/', bulkReviewTagsResultSchema, {
        reviewIds: payload.reviewIds,
        tagIds: payload.tagIds,
    });
}

export function postBulkReviewNotes(payload: BulkReviewNotesRequestType): Promise<BulkReviewNotesResultType> {
    return postAndDeserialize<BulkReviewNotesResultType>('/v4/reviews/notes/bulk/', bulkReviewNotesResultSchema, {
        reviewIds: payload.reviewIds,
        text: payload.text,
    });
}

export function fetchReviewSentimentUpdate(reviewId: number, data: {sentiment: ReviewSentimentType}): Promise<void> {
    return fetchX<void>(`${rootApiUrl}/v4/reviews/${reviewId}/sentiment/`, {
        method: FetchMethodEnum.patch,
        headers: {...mainApiHeaders, ...getCsrfHeaders()},
        body: JSON.stringify(data),
    });
}
